<template>
  <!-- ================= LOADING OVERLAY ================= -->
  <div v-if="loading" class="loading-overlay">
    <md-progress-spinner
        md-mode="indeterminate"
        :md-diameter="60"
        :md-stroke="6">
    </md-progress-spinner>

    <div class="loading-text">
      Loading details...
    </div>
  </div>

  <!-- ================= ACTUAL CONTENT ================= -->
  <div v-else>

    <!-- 🔽 EVERYTHING YOU ALREADY HAVE GOES HERE 🔽 -->
    <div v-cloak>
      <div class="md-layout">
        <div class="md-layout-item md-medium-size-33 md-small-size-50">
          <md-card class="sheet-summary-table compact-2col">

            <!-- HEADER -->
            <md-card-header class="header">
              <md-icon>receipt_long</md-icon>

              <div>
                <div class="title">Labour Utilization Sheet</div>
                <div class="sub">Sheet No. {{ invoice.invoice_number || '-' }}</div>
              </div>
            </md-card-header>

            <!-- TABLE -->
            <md-card-content>

              <table class="info-table">

                <tbody>

                <!-- ROW 1: Station + Port -->
                <tr>
                  <td>
                    <md-icon>place</md-icon>
                  </td>
                  <td>Station</td>
                  <td>
                    {{ invoice && invoice.department && invoice.department.port && invoice.department.port.station
                      ? invoice.department.port.station.name : '-' }}
                  </td>

                  <td>
                    <md-icon>anchor</md-icon>
                  </td>
                  <td>Port</td>
                  <td>
                    {{ invoice && invoice.department && invoice.department.port
                      ? invoice.department.port.name : '-' }}
                  </td>
                </tr>

                <!-- ROW 2: Department + Type -->
                <tr>
                  <td>
                    <md-icon>domain</md-icon>
                  </td>
                  <td>Department</td>
                  <td>
                    {{ invoice && invoice.department ? invoice.department.name : '-' }}
                  </td>

                  <td>
                    <md-icon>category</md-icon>
                  </td>
                  <td>Type</td>
                  <td>
                    {{ invoice && invoice.guidance_type ? invoice.guidance_type : '-' }}
                    ({{ invoice && invoice.pay_code ? invoice.pay_code : '-' }})
                  </td>
                </tr>

                <!-- ROW 3: Period + Date -->
                <tr>
                  <td>
                    <md-icon>date_range</md-icon>
                  </td>
                  <td>Period</td>
                  <td>
                    {{ invoice && invoice.start_date && invoice.end_date
                      ? formatDate(invoice.start_date) + ' → ' + formatDate(invoice.end_date)
                      : '-' }}
                  </td>

                  <td>
                    <md-icon>event</md-icon>
                  </td>
                  <td>Date</td>
                  <td>
                    {{ formatDate(invoice ? invoice.invoice_date : null) }}
                  </td>
                </tr>

                <!-- ROW 4: Status -->
                <tr>
                  <td>
                    <md-icon>info</md-icon>
                  </td>
                  <td>Status</td>
                  <td>
                    <md-chip :class="statusClass">
                      {{ invoice && invoice.status ? invoice.status : 'Draft' }}
                    </md-chip>
                  </td>
                  <td>
                    <md-icon>business</md-icon>
                  </td>
                  <td>
                    Vendor
                  </td>
                  <td>
                    {{ invoice && invoice.vendor ? invoice.vendor.name : '-' }}
                  </td>
                </tr>

                </tbody>

              </table>

            </md-card-content>

            <!-- ACTIONS -->
            <md-card-actions class="actions">

              <md-button
                  v-if="invoice && invoice.voucher && invoice.voucher.id"
                  class="md-info md-round"
                  @click="goToViewVoucher(invoice.voucher.id)"
              >
                <md-icon>summarize</md-icon>
                Summary
              </md-button>

              <md-button class="md-primary md-round" @click="goBack">
                <md-icon>arrow_back</md-icon>
                Back
              </md-button>

              <md-button
                  v-if="!invoice.status"
                  class="md-warning md-round"
                  @click="goToEdit(invoice.id)"
              >
                <md-icon>edit</md-icon>
                Edit
              </md-button>

            </md-card-actions>

          </md-card>
        </div>

      </div>
      <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
        <nav-tabs-card>
          <template slot="content">
            <span class="md-nav-tabs-title">Utilization:</span>
            <md-tabs class="md-info" md-alignment="left">

              <md-tab id="tab-home" md-label="Sheet Items" md-icon="view_list">
                <!-- ======== FILTER SECTION ======== -->
                <md-card class="filter-card">

                  <md-card-header>
                    <h4 class="md-title">Filters</h4>
                  </md-card-header>

                  <md-card-content>

                    <div class="md-layout md-gutter">

                      <!-- Start Date -->
                      <div class="md-layout-item md-size-20 md-small-size-50">
                        <md-datepicker
                            v-model="filters.start_date"
                            :md-model-type="String"
                            md-immediately
                        >
                          <label>Start Date</label>
                        </md-datepicker>
                      </div>

                      <!-- End Date -->
                      <div class="md-layout-item md-size-20 md-small-size-50">
                        <md-datepicker
                            v-model="filters.end_date"
                            :md-model-type="String"
                            md-immediately
                        >
                          <label>End Date</label>
                        </md-datepicker>
                      </div>

                      <!-- Labourer -->
                      <div class="md-layout-item md-size-20 md-small-size-50">
                        <label class="filter-label">Labourer</label>

                        <v-select
                            v-model="filters.labourer"
                            :options="availableLabourersForSelect"
                            :reduce="item => item.id"
                            label="full_name"
                            placeholder="Search labourer..."
                            clearable
                        >
                          <template #option="{ first_name, middle_name, last_name }">
                            {{ first_name }} {{ middle_name }} {{ last_name }}
                          </template>

                          <template #selected-option="{ first_name, middle_name, last_name }">
                            {{ first_name }} {{ middle_name }} {{ last_name }}
                          </template>
                        </v-select>
                      </div>

                      <!-- Shift -->
                      <div class="md-layout-item md-size-20 md-small-size-50">
                        <label class="filter-label">Shift</label>

                        <v-select
                            v-model="filters.shift"
                            :options="availableShifts"
                            placeholder="Search shift..."
                            clearable
                        />
                      </div>

                      <!-- Workplace -->
                      <div class="md-layout-item md-size-20 md-small-size-50">
                        <label class="filter-label">Workplace</label>

                        <v-select
                            v-model="filters.workplace"
                            :options="availableWorkplaces"
                            placeholder="Search workplace..."
                            clearable
                        />
                      </div>

                    </div>

                    <div class="filter-actions">

                      <md-button
                          class="md-dense"
                          @click="resetFilters"
                      >
                        <md-icon>refresh</md-icon>
                        Reset
                      </md-button>

<!--                      <md-button-->
<!--                          class="md-success md-dense md-round"-->
<!--                          @click="exportExcel"-->
<!--                      >-->
<!--                        <md-icon>grid_on</md-icon>-->
<!--                        Export Excel-->
<!--                      </md-button>-->

<!--                      <md-button-->
<!--                          class="md-info md-dense md-round"-->
<!--                          @click="exportPDF"-->
<!--                      >-->
<!--                        <md-icon>picture_as_pdf</md-icon>-->
<!--                        Preview PDF-->
<!--                      </md-button>-->

<!--                      <md-button-->
<!--                          class="md-raised md-success md-dense md-round"-->
<!--                          @click="downloadPDF"-->
<!--                      >-->
<!--                        <md-icon>download</md-icon>-->
<!--                        Download PDF-->
<!--                      </md-button>-->

                      <md-button
                          class="md-primary"
                          :disabled="generatingPdf || pdfReady"
                          @click="generatePdf">

                        <md-progress-spinner
                            v-if="generatingPdf"
                            md-mode="indeterminate"
                            :md-diameter="18"
                            :md-stroke="2"/>

                        {{ generatingPdf ? 'Generating PDF...' : (pdfReady ? 'PDF Generated' : 'Generate PDF') }}

                      </md-button>

                      <md-button
                          class="md-info"
                          :disabled="!pdfReady"
                          @click="previewPdf">

                        <md-icon>visibility</md-icon>

                        Preview PDF

                      </md-button>

                      <md-button
                          class="md-success"
                          :disabled="!pdfReady"
                          @click="downloadPdf">

                        <md-icon>download</md-icon>

                        Download PDF

                      </md-button>


                    </div>
                    <div
                        v-if="downloading"
                        class="mt-3">

                      <md-progress-bar
                          md-mode="indeterminate">
                      </md-progress-bar>

                      <div class="text-muted mt-2">

                        Loading PDF...

                      </div>

                    </div>
                    <md-progress-bar
                        v-if="generatingPdf"
                        md-mode="indeterminate" />
                  </md-card-content>

                </md-card>


                <div class="md-layout">

                  <div class="md-layout-item md-size-80 md-size-80 md-size-80 md-size-100-small">
                    <md-card :key="index1=0">
                      <!--                                        <div class="card-icon">-->
                      <!--                                            <md-icon class="md-size-2x">home</md-icon>-->
                      <!--                                        </div>-->
                      <div class="md-layout-item md-size-20 md-size-20 md-size-20 md-size-100-small">
                        <md-field>
                          <label>Items per page</label>
                          <md-select v-model="pagination.perPage">
                            <md-option v-for="size in pageSizeOptions" :key="size" :value="size">
                              {{ size }}
                            </md-option>
                          </md-select>
                        </md-field>
                      </div>

                      <div v-for="(type,typeIndex) in invoice_types" :key="typeIndex">

                        <md-card-header class="md-card-header-icon md-card-header-green">
                        </md-card-header>
                        <md-card-content>
                          <hr />
                          <div v-if="pdfPreviewUrl" class="pdf-container" style="margin-top: 20px;">
                            <md-button
                                class="md-icon-button md-dense close-btn"
                                @click="pdfPreviewUrl = null"
                                title="Close Preview"
                            >
                              <i class="material-icons">close</i>
                            </md-button>
                            <iframe
                                :src="pdfPreviewUrl"
                                style="width: 100%; height: 90vh; border: 1px solid #ccc; border-radius: 8px;"
                            ></iframe>
                            <md-button class="md-accent" @click="pdfPreviewUrl = null">
                              Close Preview
                            </md-button>

                          </div>
                          <md-table>
                            <md-table-row>
                              <md-table-head md-numeric>SN</md-table-head>
                              <md-table-head>Labourer Name</md-table-head>
                              <md-table-head>Labourer ID</md-table-head>
                              <md-table-head>TakeOn Number</md-table-head>
                              <md-table-head>Guidance Number</md-table-head>
                              <md-table-head>Date</md-table-head>
                              <md-table-head>Work place</md-table-head>
                              <md-table-head>Activity</md-table-head>
                              <md-table-head>Shift</md-table-head>
                              <!--                                                        <md-table-head md-numeric>Shift Hours</md-table-head>-->
                              <!--                                                        <md-table-head md-numeric>Labourer Count</md-table-head>-->
                              <md-table-head md-numeric>Worked Hours</md-table-head>
                              <md-table-head md-numeric>Rate</md-table-head>
                              <md-table-head md-numeric>Cost</md-table-head>
                            </md-table-row>
                            <!--                                                    <md-table-row-->
                            <!--                                                            v-for="(item,ind) in filteredInvoiceTypes(type)" :key="item.id || ind"> -->
                            <md-table-row
                                v-for="(item,ind) in paginatedInvoiceItems(type)" :key="item ? item.id || ind : ind">
                              <md-table-cell md-numeric>
                                {{ind+1}}
                              </md-table-cell>
                              <md-table-cell>
                                {{item.labourer?item.labourer.first_name+' '+item.labourer.middle_name+' '+item.labourer.last_name:''}}
                              </md-table-cell>
                              <md-table-cell>
                                {{
                                  item.labourer && item.labourer.vendor_record
                                      ? item.labourer.vendor_record.vendor_labourer_number
                                      : ''
                                }}
                              </md-table-cell>
                              <md-table-cell>
                                {{ item.labourerSchedule ? item.labourerSchedule.takeon_number : '' }}
                              </md-table-cell>
                              <md-table-cell>
                                {{ item.guidance ? item.guidance.guidance_number : '' }}
                              </md-table-cell>
                              <md-table-cell>
                                {{item.date}}
                              </md-table-cell>

                              <md-table-cell>
                                {{item.workplace}}
                              </md-table-cell>
                              <md-table-cell>
                                {{item.activity}}
                              </md-table-cell>
                              <md-table-cell>
                                {{item.name}}
                              </md-table-cell>
                              <!--                                                      <md-table-cell v-if="item.invoice_type==type">-->
                              <!--                                                        {{item.hours}}-->
                              <!--                                                      </md-table-cell>-->
                              <!--                                                      <md-table-cell v-if="item.invoice_type==type">-->
                              <!--                                                        {{item.labourer_count}}-->
                              <!--                                                      </md-table-cell>-->

                              <md-table-cell md-numeric>
                                {{item.quantity}}
                              </md-table-cell>
                              <md-table-cell md-numeric>
                                {{item.rate}}
                              </md-table-cell>
                              <md-table-cell md-numeric>
                                {{currencyFormat(lineTotal(item.quantity,item.rate))}}
                              </md-table-cell>
                            </md-table-row>

                            <md-table-row :key="'subtotal'">
                              <md-table-cell>
                              </md-table-cell>
                              <md-table-cell>
                              </md-table-cell>
                              <md-table-cell>

                              </md-table-cell>

                              <md-table-cell>
                              </md-table-cell>
                              <md-table-cell>
                              </md-table-cell>
                              <md-table-cell>
                              </md-table-cell>

                              <md-table-cell>
                                <b>Sub Total</b>
                              </md-table-cell>
                              <md-table-cell>
                              </md-table-cell>
                              <md-table-cell>

                              </md-table-cell>
                              <md-table-cell>
                              </md-table-cell>

                              <md-table-cell>
                                {{ currencyFormat(filteredTypeSubtotal(type)) }}
                              </md-table-cell>
                              <md-table-cell md-numeric>
                                <!--                                                            <b> {{currencyFormat(subTotal(type))}}</b>-->
                              </md-table-cell>
                            </md-table-row>
                            <md-table-row :key="'vat'">
                              <md-table-cell>
                              </md-table-cell>
                              <md-table-cell>
                              </md-table-cell>
                              <md-table-cell>

                              </md-table-cell>

                              <md-table-cell>
                              </md-table-cell>
                              <md-table-cell>
                              </md-table-cell>
                              <md-table-cell>
                              </md-table-cell>

                              <md-table-cell>
                                <b>VAT ({{invoice.vat_rate}}%)</b>
                              </md-table-cell>
                              <md-table-cell>
                              </md-table-cell>
                              <md-table-cell>

                              </md-table-cell>
                              <md-table-cell>
                              </md-table-cell>

                              <md-table-cell>
                                {{ currencyFormat(invoice.vat) }}
                              </md-table-cell>
                              <md-table-cell md-numeric>
                                <!--                                                            <b> {{currencyFormat(subTotal(type))}}</b>-->
                              </md-table-cell>
                            </md-table-row>
                            <md-table-row :key="'grand'">
                              <md-table-cell>
                              </md-table-cell>
                              <md-table-cell>
                              </md-table-cell>
                              <md-table-cell>

                              </md-table-cell>

                              <md-table-cell>
                              </md-table-cell>
                              <md-table-cell>
                              </md-table-cell>
                              <md-table-cell>
                              </md-table-cell>

                              <md-table-cell>
                                <b>Grand Total</b>
                              </md-table-cell>
                              <md-table-cell>
                              </md-table-cell>
                              <md-table-cell>
                              </md-table-cell>

                              <md-table-cell>
                              </md-table-cell>

                              <md-table-cell>
                                {{ currencyFormat(invoice.amount+invoice.vat) }}
                              </md-table-cell>
                              <md-table-cell md-numeric>
                                <!--                                                            <b> {{currencyFormat(subTotal(type))}}</b>-->
                              </md-table-cell>
                            </md-table-row>
                          </md-table>
                          <!-- Pagination -->
                          <md-card-actions md-alignment="space-between">
                            <div>
                              <p class="card-category">
                                Showing {{ from + 1 }} to {{ to }} of {{ totalItems(type) }} entries
                              </p>
                            </div>
                            <pagination
                                v-model="pagination.currentPage"
                                :per-page="pagination.perPage"
                                :total="totalItems(type)"
                            />
                          </md-card-actions>


                          <div>

                          </div>

                        </md-card-content>

                      </div>
                      <hr/>
                      <div class="md-layout" v-if="can('list-invoice-signature')">
                        <div class="md-layout-item" v-for="status in createdStatuses" :key="status.id || status.creator_id">
                          <b>Prepared by: <br><u>
                            <span v-html="creatorLabel(status)"></span>
                          </u><br/>
                            Signature: <img :src="signature+status.attachment" style="width: 100px;"/><br/>
                            Date: {{new Date(status.created_at).toLocaleDateString()}}</b>
                        </div>

                        <div class="md-layout-item" v-for="status in reviewedStatuses" :key="status.id || status.creator_id">
                          <b>Reviewed by: <br> <u>
                            <span v-html="creatorLabel(status)"></span>

                          </u><br/>
                            Signature: <img :src="signature+status.attachment" style="width: 100px;"/><br/>
                            Date: {{new Date(status.created_at).toLocaleDateString()}}</b>
                        </div>
                        <div class="md-layout-item" v-for="status in certifiedStatuses" :key="status.id || status.creator_id">
                          <b>Certified by: <u>

                            <span v-html="creatorLabel(status)"></span>
                          </u><br/>
                            Signature: <img :src="signature+status.attachment" style="width: 100px;"/><br/>
                            Date: {{new Date(status.created_at).toLocaleDateString()}}</b>
                        </div>
                        <div class="md-layout-item" v-for="status in approvedStatuses" :key="status.id || status.creator_id">
                          <b>Approved by:<br> <u>
                            <!--                                              {{status.creator?status.creator.name:status.creator_id}}-->
                            <span v-html="creatorLabel(status)"></span>
                          </u><br/>
                            Signature: <img :src="signature+status.attachment" style="width: 100px;"/><br/>
                            Date: {{new Date(status.created_at).toLocaleDateString()}}</b>
                        </div>
                        <div class="md-layout-item" v-for="status in confirmedStatuses" :key="status.id || status.creator_id">
                          <b>Confirmed by:<br> <u>
                            <!--                                              {{status.creator?status.creator.name:status.creator_id}}-->
                            <span v-html="creatorLabel(status)"></span>
                          </u><br/>
                            Signature: <img :src="signature+status.attachment" style="width: 100px;"/><br/>
                            Date: {{new Date(status.created_at).toLocaleDateString()}}</b>
                        </div>

                      </div>

                      <div class="md-layout-item">
                        <div v-if="(can('create-invoice-status')&&(invoice.status==''))">
                          <md-button class="md-info md-round md-dense classic-modal"
                                     @click="modal.classicModal = true"
                          >
                            Submit for Review
                          </md-button>
                        </div>
                        <div v-else-if="can('review-invoice-status')&&invoice.status=='Created'">
                          <md-button class="md-info md-round md-dense classic-modal"
                                     @click="modal.classicModal = true"
                          >
                            Review
                          </md-button>
                          <md-button class="md-danger md-round md-dense classic-modal"
                                     @click="reject=true,modal.classicModal = true"
                          >
                            Reject
                          </md-button>
                        </div>
                        <div v-else-if="can('certify-invoice-status') && invoice.status=='Reviewed'">
                          <md-button class="md-info md-round md-dense classic-modal"
                                     @click="modal.classicModal = true"
                          >
                            Certify
                          </md-button>
                          <md-button class="md-danger md-round md-dense classic-modal"
                                     @click="reject=true,modal.classicModal = true"
                                     v-if="can('reject-invoice-status')"
                          >
                            Reject
                          </md-button>
                        </div>
                        <div v-else-if="can('confirm-invoice-status') && invoice.status=='Certified'">
                          <md-button class="md-info md-round md-dense classic-modal"
                                     @click="modal.classicModal = true"
                          >
                            Confirm
                          </md-button>
                          <md-button class="md-danger md-round md-dense classic-modal"
                                     @click="reject=true,modal.classicModal = true"
                                     v-if="can('reject-invoice-status')"
                          >
                            Reject
                          </md-button>
                        </div>
                        <div  v-else-if="canRejectconfirmed(invoice)">
                          <!--                                            <md-button class="md-info md-round md-dense classic-modal"-->
                          <!--                                                       @click="modal.classicModal = true"-->
                          <!--                                                      >-->
                          <!--                                                Accept for Payment-->
                          <!--                                            </md-button>-->
                          <md-button class="md-danger md-round md-dense classic-modal"
                                     @click="reject=true,modal.classicModal = true"
                                     v-if="can('reject-invoice-status')"
                          >
                            Reject
                          </md-button>
                        </div>

                      </div>
                    </md-card>
                  </div>
                  <!--        Start-->
                  <div class="md-layout-item md-medium-size-20 md-xsmall-size-100 md-size-20">
                    <md-card-content>
                      <time-line plain type="simple" v-if="can('list-invoice-status')">
                        <b>Status</b>

                        <time-line-item v-for="(status, stindex) in invoice.statuses_with_trashed"
                                        :key="status.id ||stindex"
                                        :label="status.name"
                                        :value="status.name"
                                        inverted
                                        :badge-type="status.deleted_at===null?'info':'danger'"
                                        badge-icon="label_important_outlined">
                                                <span slot="header"
                                                      :class="status.deleted_at===null?'badge badge-info':'badge badge-danger'">{{status.name}}</span>

                          <div slot="content">
                            {{status.reason}}

                          </div>
                          <h6 slot="footer">
                            <i class="ti-time"> </i>
                            <hr>
                            By:
                            <span v-html="creatorLabel(status)"></span>
                            <!--                                                    {{status.creator?status.creator.name:status.creator_id}}-->
                            <br>
                            {{new Date(status.created_at).toLocaleString()}}
                          </h6>
                        </time-line-item>
                      </time-line>
                    </md-card-content>
                  </div>
                  <!--        End-->
                </div>
              </md-tab>


            </md-tabs>
            <modal v-if="modal.classicModal" @close="classicModalHide">
              <template slot="header">
                <h4 class="modal-title">Change status</h4>
                <md-button class="md-simple md-just-icon md-round modal-default-button"
                           @click="classicModalHide">
                  <md-icon>clear</md-icon>
                </md-button>
              </template>

              <div slot="body">
                <!--                Parked Status-->
                <div v-if="can('create-invoice-status')&&invoice.status==''">
                  <p>Ensure that the Invoice is OK then write your comment for Submition (option) and click
                    Submit for Review button</p>

                  <form @submit.prevent="update">
                    <md-card-content>
                      <div class="md-layout">
                        <div class="md-layout-item">
                          <md-field class="md-invalid">
                            <md-input v-model="status.reason" placeholder="Reason/Comment"/>
                            <validation-error :errors="apiValidationErrors.reason"/>
                          </md-field>
                        </div>
                      </div>
                    </md-card-content>

                    <md-button @click="update" class="md-primary md-dense">
                      Submit for Review
                    </md-button>
                  </form>
                </div>
                <!--                Reject Status-->
                <div v-else-if="reject">
                  <p>Please provide reason(s) for rejection
                    Reject button</p>

                  <form @submit.prevent="update">
                    <md-card-content>
                      <div class="md-layout">
                        <div class="md-layout-item">
                          <md-field class="md-invalid">
                            <md-input v-model="status.reason" placeholder="Reasons for Rejection"/>
                            <validation-error :errors="apiValidationErrors.reason"/>
                          </md-field>
                        </div>
                      </div>
                    </md-card-content>

                    <md-button @click="update" class="md-danger md-dense">
                      Reject
                    </md-button>
                  </form>
                </div>
                <!--                Reviewed Status-->
                <div v-else-if="can('review-invoice-status')&&invoice.status=='Created'">
                  <p>Ensure that the Invoice is OK then write your Review comment and click
                    Review Button</p>

                  <form @submit.prevent="update">
                    <md-card-content>
                      <div class="md-layout">
                        <div class="md-layout-item">
                          <md-field class="md-invalid">
                            <md-input v-model="status.reason" placeholder="Reason/Comment"/>
                            <validation-error :errors="apiValidationErrors.reason"/>
                          </md-field>
                        </div>
                      </div>
                    </md-card-content>

                    <md-button @click="update" class="md-primary md-dense">
                      Review
                    </md-button>
                  </form>
                </div>
                <!--                Certified Status-->
                <div v-else-if="can('certify-invoice-status')&&invoice.status=='Reviewed'">
                  <p>Ensure that the Invoice is OK write your comment for Certification (option) and click
                    Certify button</p>

                  <form @submit.prevent="update">
                    <md-card-content>
                      <div class="md-layout">
                        <div class="md-layout-item">
                          <md-field class="md-invalid">
                            <md-input v-model="status.reason" placeholder="Reason/Comment"/>
                            <validation-error :errors="apiValidationErrors.reason"/>
                          </md-field>
                        </div>
                      </div>
                    </md-card-content>

                    <md-button @click="update" class="md-primary md-dense">
                      Certify
                    </md-button>
                  </form>
                </div>
                <!--                Confirmed Status-->
                <div v-else-if="can('confirm-invoice-status')&&invoice.status=='Certified'">
                  <p>Ensure that the Invoice is OK write your comment for Confirmation (option) and click
                    Confirm button</p>

                  <form @submit.prevent="update">
                    <md-card-content>
                      <div class="md-layout">
                        <div class="md-layout-item">
                          <md-field class="md-invalid">
                            <md-input v-model="status.reason" placeholder="Reason/Comment"/>
                            <validation-error :errors="apiValidationErrors.reason"/>
                          </md-field>
                        </div>
                      </div>
                    </md-card-content>

                    <md-button @click="update" class="md-primary md-dense">
                      Confirm
                    </md-button>
                  </form>
                </div>
                <!--                Certified Status-->
                <div v-else-if="can('accept-invoice-status')&&invoice.status=='Approved'">
                  <p>Ensure that the Invoice is OK then write your comment for Acceptance (option) and click
                    Accept button</p>

                  <form @submit.prevent="update">
                    <md-card-content>

                      <div class="md-layout">
                        <div class="md-layout-item">
                          <md-field class="md-invalid">
                            <md-input v-model="status.reason" placeholder="Reason/Comment"/>
                            <validation-error :errors="apiValidationErrors.reason"/>
                          </md-field>
                        </div>
                      </div>
                    </md-card-content>

                    <md-button @click="update" class="md-primary md-dense">
                      Accept
                    </md-button>
                  </form>
                </div>

                <p v-else-if="invoice.status=='Confirmed'">
                  It is Confirmed by
                  {{invoice.statuses[2].creator?invoice.statuses[2].creator.name:invoice.statuses[2].creator_id}}<br>
                  Date: {{new Date(invoice.statuses[2].created_at).toLocaleDateString()}}
                </p>
                <p v-else class="text-danger">You are not authorized</p>
              </div>


              <template slot="footer">
                <md-button class="md-danger md-simple" @click="classicModalHide">Close</md-button>
              </template>
            </modal>
          </template>
        </nav-tabs-card>
      </div>

    </div>
  </div>
</template>
<script>
import formMixin from '@/mixins/form-mixin';
import _ from 'lodash';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {
  TimeLine,
  TimeLineItem,
  Badge,
  Modal,
  NavTabsCard,
  PricingCard,
  TestimonialCard,
  ValidationError
} from '@/components';
import Vuex from 'vuex';
import workplaces from '../../../store/modules/employers/workplaces-module';
import Swal from 'sweetalert2';
import Multiselect from 'vue-multiselect';
import leftLogo from '@/assets/images/logo_bibi_bwana.png';
import rightLogo from '@/assets/images/TPA_Logo.png';
import pagination from "@/components/Pagination.vue";

export default {
  components: {
    pagination,
    TimeLine,
    TimeLineItem,
    Badge,
    ValidationError,
    Modal,
    NavTabsCard,
    PricingCard,
    Multiselect,
    TestimonialCard
  },
  mixins: [formMixin],
  data: () => ({
    tabs: {
      selected: [],
      responsive: false,
      firstTabs: [],
      secondTabs: [],
      thirdTabs: [],
    },
    loading: true,   // 👈 IMPORTANT
    pdfPreviewUrl: null, // used to show the generated PDF
    pdfBlob: null, // store blob for downloading
    downloading:false,
    roles: [],
    statuses: [],
    allPermissions: [],
    responsive: true,// Timeline
    table: [],
    selected_contributions:[],
    footerTable: [
      'Name',
      'Call ID',
      'Vessel',
      'Created At',
      'Actions'],

    query: null,

    sortation: {
      field: 'created_at',
      order: 'asc',
    },

    // pagination: {
    //     perPage: 10,
    //     currentPage: 1,
    //     perPageOptions: [5, 10, 25, 50],
    // },

    total: 0,
    request_type: {},
    items: [],
    invoiceData: {},
    invoice_types:[],
    departments:[],
    guidances:[],
    generatingPdf: false,
    pdfReady: false,
    pdfStatus: 'PENDING',

    invoice: {
      type: 'invoices',
      invoice_number:null,
      invoice_date:null,
      payment_mode:null,
    },
    status: {
      name: null,
      reason: '',
    },
    reject:false,
    modal: {
      classicModal: false,
      updateDetailModal: false,
      addDetailModal: false,
      addWorkItemModal: false,
      updateWorkItemModal: false,
    },
    signature: '',

    filters: {
      start_date: '',
      end_date: '',
      labourer: '',
      shift: '',
      workplace: '',
    },
    currentType: '', // current invoice type being viewed
    pagination: {
      currentPage: 1,
      perPage: 10
    },
    pageSizeOptions: [5, 10, 20, 50, 100], // dropdown options
    availableLabourers: [],
    availableShifts: [],
    availableWorkplaces: [],


  }),


  created () {
    this.get();
    this.currentType = this.invoice_types[0] || '';
    this.getDepartments();
    this.signature = process.env.VUE_APP_IMG_URL;
  },

  methods: {
    canRejectconfirmed(invoice) {
      if (!invoice) {
        return false;
      }
      const locked = invoice.voucher ? invoice.voucher.locked : 0;
      return (
          (invoice.status === 'Confirmed' && this.can('confirm-invoice-status') && (locked === null || locked === 0)) ||
          (this.can('approve-voucher-status')&& locked === 0)
      );
    },

    currencyFormat (value) {
      if (!value && value !== 0) {
        return '';
      }
      return Number(value).toLocaleString('en-US'); // e.g. 1,200,000
    },
    lineTotal (quantity,rate) {
      return quantity * rate;
    },

    subTotal (type) {
      const v = this.items.filter(s => s.invoice_type == type);
      return v.reduce((total_amount, item) => item.rate * item.quantity + total_amount  ,0);
    },
    invoiceTypes (type) {
      const filtered = this.items.filter(s => s.invoice_type == type);
      if (filtered.length > 0) {
        return filtered;
      } else {
        return [];
      }
    },
    filteredInvoiceTypes (type) {
      const filtered = this.filteredItems.filter(s => s.invoice_type == type);
      return filtered.length > 0 ? filtered : [];
    },

    classicModalHide: function () {
      this.modal.classicModal = false;
      this.reject = false;
    },
    updateDetailModalHide: function () {
      this.modal.updateDetailModal = false;
    },

    async update () {
      try {
        this.invoiceData = {
          type: 'invoices',
          id: this.invoice.id,
          status: { name: this.status.name, reason: this.status.reason,reject:this.reject },
        };
        await this.$store.dispatch('invoices/update1', this.invoiceData);
        await this.$store.dispatch(
            'alerts/success',
            'Invoice updated successfully.'
        );
        // this.get();
        this.$router.go();
      } catch (e) {
        if (e.response.data.errors[0]) {
          await this.$store.dispatch(
              'alerts/error',
              e.response.data.errors[0].detail
          );
          this.setApiValidation(e.response.data.errors);
        } else {
          await this.$store.dispatch(
              'alerts/error',
              'Oops, something went wrong!1'
          );
        }
      }
    },
    onResponsiveInverted () {
      if (window.innerWidth < 768) {
        this.responsive = false;
      } else {
        this.responsive = false;
      }
    },
    getListDebounced: _.debounce(function () {
      this.get();
    }, 300),

    async get () {
      this.loading = true
      try {

        await this.$store.dispatch('invoices/get',
            {
              id: this.$route.params.id,
              include: 'invoiceItems,invoiceItems.guidance,department.port.station,vendor,voucher'
            });
        this.invoice = this.$store.getters['invoices/invoice'];
        this.statuses = this.invoice.statuses;
        this.items = this.invoice.invoiceItems;
        this.tabs.firstTabs = this.invoice.invoiceItems;
        const contr = this.invoice.distributions;
        this.selected_contributions = this.invoice.distributions.filter(s => s.is_hidden == false);
        const v = this.invoice.invoiceItems;
        const departments = [...new Set(v.map(item => item.department))];
        const invoice_types = [...new Set(v.map(item => item.invoice_type))];
        const guidances = [...new Set(v.map(item => item.guidance))];
        this.invoice_types = invoice_types;
        //  Set currentType AFTER invoice_types is loaded
        if (this.invoice_types.length > 0) {
          this.currentType = this.invoice_types[0];
        }

        this.departments = departments;
        this.guidances = guidances;
        const labourers = [
          ...new Map(
              this.invoice.invoiceItems
                  .filter(i => i.labourer)
                  .map(i => [i.labourer.id, i.labourer])
          ).values(),
        ];
        this.availableLabourers = labourers;

        this.availableShifts = [...new Set(this.invoice.invoiceItems.map(i => i.name))];
        this.availableWorkplaces = [...new Set(this.invoice.invoiceItems.map(i => i.workplace))];
      } catch (e) {
        await this.$store.dispatch(
            'alerts/error',
            'Oops, something went wrong!2'
        );
      }finally {
        this.pdfReady = this.invoice.pdf_status === 'COMPLETED';
        this.pdfStatus = this.invoice.pdf_status;
        setTimeout(() => {
          this.loading = false
        }, 300)
      }
    },

    async getItem (id) {
      try {
        console.log(id);
        await this.$store.dispatch('invoiceItems/get', id);
        this.item = this.$store.getters['invoiceItems/invoiceItem'];
        console.log(this.item);
      } catch (e) {
        await this.$store.dispatch(
            'alerts/error',
            'Oops, something went wrong!3'
        );
      }
    },
    geItems () {
      // console.log(this.available_workItems_dropdown);
      this.$store.dispatch('invoiceItems/list').then(() => {
        //  console.log(this.$store)
        this.available_invoiceItem_dropdown = this.$store.getters['invoiceItems/list'];
        // console.log(this.available_activities_dropdown)
      });
    },
    getDepartments () {
      this.$store.dispatch('departments/list').then(() => {
        this.available_departments = this.$store.getters['departments/dropdown'];
      });
    },
    goBack () {
      this.$router.push({ name: 'Utilization Sheet List' });
    },

    customSort () {
      return false;
    },

    resetFilters () {
      this.generatingPdf = false;
      this.pdfReady = false;
      this.filters = {
        start_date: '',
        end_date: '',
        labourer: '',
        shift: '',
        workplace: '',
      };
    },
    filteredTypeSubtotal (type) {
      return this.filteredItems
          .filter(i => i.invoice_type === type)
          .reduce((t, i) => t + (parseFloat(i.quantity) * parseFloat(i.rate) || 0), 0);
    },
    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0"); // months are 0-based
      const year = d.getFullYear();
      return `${day}-${month}-${year}`; // format: DD/MM/YYYY
    },

    dayDifference (startDateStr, endDateStr) {
      if (!startDateStr || !endDateStr) {
        return '';
      }
      const start = new Date(startDateStr);
      const end = new Date(endDateStr);
      if (isNaN(start) || isNaN(end)) {
        return '';
      }
      const diffTime = end - start;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    },
    // Excel Export
    exportExcel () {
      const wb = XLSX.utils.book_new();
      // Helper: format date like "15-10-2025"
      const formatDate = (dateStr) => {
        if (!dateStr) {
          return '';
        }
        const date = new Date(dateStr);
        if (isNaN(date)) {
          return dateStr;
        } // fallback if invalid
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      };
      const dayDifference = (start, end) => {
        if (!start || !end) {
          return '';
        }
        const startDate = new Date(start);
        const endDate = new Date(end);
        if (isNaN(startDate) || isNaN(endDate)) {
          return '';
        }
        const diffTime = endDate - startDate;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
        return diffDays;
      };
      this.invoice_types.forEach(type => {
        const itemsOfType = this.filteredItems.filter(i => i.invoice_type === type);
        // Prepare data
        const data = itemsOfType.map((item, index) => ({
          SN: index + 1,
          'Labourer Name': item.labourer
              ? `${item.labourer.first_name} ${item.labourer.middle_name} ${item.labourer.last_name}`
              : '',
          'Labourer ID': item.labourer && item.labourer.vendor_record
              ? `${item.labourer.vendor_record.vendor_labourer_number}`
              : '',
          'TakeOn Number': item.labourerSchedule && item.labourerSchedule.takeon_number
              ? item.labourerSchedule.takeon_number
              : '',
          'Guidance Number': item.guidance
              ? item.guidance.guidance_number
              : '',
          Date: item.date,

          Workplace: item.workplace,
          Activity: item.activity,
          Shift: item.name,
          'Worked Hours': item.quantity,
          'Rate(TZS)': item.rate,
          'Cost(TZS)': item.quantity * item.rate
        }));

        // Add subtotal row
        data.push({
          SN: '',
          'Labourer Name': '',
          'Labourer ID': '',
          'TakeOn Number': '',
          'Guidance Number': '',
          Date: '',
          Workplace: '',
          Activity: 'Subtotal',
          Shift: '',
          'Worked Hours': '',
          'Rate(TZS)': '',
          'Cost(TZS)': itemsOfType.reduce((sum, i) => sum + (i.quantity * i.rate), 0)
        });

        const ws = XLSX.utils.json_to_sheet(data, { origin: 7 }); // leave space for headings

        // Add page heading manually
        XLSX.utils.sheet_add_aoa(ws, [
          [`Station: ${this.invoice.department.port.station.name}      Port: ${this.invoice.department.port.name}`],
          [`Department: ${this.invoice.department.name}`],
          [`Utilization Sheet No. ${this.invoice.invoice_number}`],
          [`Date: ${this.formatDate(this.filters.start_date || this.invoice.start_date)} to ${this.formatDate(this.filters.end_date || this.invoice.end_date)} ( ${this.dayDifference(this.filters.start_date || this.invoice.start_date, this.filters.end_date || this.invoice.end_date) } days)`],
          [`Guidance Type: ${type}`],
          []
        ], { origin: 0 });

        XLSX.utils.book_append_sheet(wb, ws, `${type}`);
      });

      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `Utilization_Sheet_${this.invoice.invoice_number}.xlsx`);
    },
    downloadPDF() {
      if (!this.pdfBlob) return;
      const url = URL.createObjectURL(this.pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Voucher_${(this.voucher && this.voucher.voucher_number) || "Summary"}.pdf`;
      link.click();
      URL.revokeObjectURL(url); // clean up
    },
    // PDF Export
    exportPDF () {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
      });
      //Helper function for signature
      const addSignatures = (doc, signatureData) => {
        // Get Y position after the last table
        let lastY = doc.autoTable.previous ? doc.autoTable.previous.finalY : 50;
        const marginTop = 15; // space after table
        const startY = lastY + marginTop;

        const columnWidth = (doc.internal.pageSize.getWidth() - 28) / signatureData.length;

        signatureData.forEach((item, i) => {
          const x = 14 + i * columnWidth;

          // Label
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(10);
          doc.text(item.label, x, startY);

          // Name
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          doc.text(item.name, x, startY + 5);

          // Space for signature (actual signature can go here)
          const signatureY = startY + 15;

          // Dot-dot line BELOW signature space
          const dotLineY = signatureY;
          const dotCount = Math.floor((columnWidth - 10) / 1.8); // adjust spacing
          const dotLine = '.'.repeat(dotCount);
          doc.setFontSize(14);
          doc.text(dotLine, x, dotLineY);

          // Date below dot line
          const dateY = dotLineY + 5;
          doc.setFontSize(9);
          doc.text(item.date, x, dateY);
        });
      };

      const signatureData = [
        ...this.createdStatuses.map(s => ({
          label: 'Prepared by:',
          // name: s.creator ? s.creator.name : s.creator_id,
          name: this.creatorLabelPdf(s),
          attachment: s.attachment ? this.signature + s.attachment : null,
          date: new Date(s.created_at).toLocaleDateString(),
        })),
        ...this.reviewedStatuses.map(s => ({
          label: 'Reviewed by:',
          // name: s.creator ? s.creator.name : s.creator_id,
          name: this.creatorLabelPdf(s),
          attachment: s.attachment ? this.signature + s.attachment : null,
          date: new Date(s.created_at).toLocaleDateString(),
        })),
        ...this.certifiedStatuses.map(s => ({
          label: 'Certified by:',
          // name: s.creator ? s.creator.name : s.creator_id,
          name: this.creatorLabelPdf(s),
          attachment: s.attachment ? this.signature + s.attachment : null,
          date: new Date(s.created_at).toLocaleDateString(),
        })),
        ...this.approvedStatuses.map(s => ({
          label: 'Approved by:',
          name: this.creatorLabelPdf(s),
          // name: s.creator ? s.creator.name : s.creator_id,
          attachment: s.attachment ? this.signature + s.attachment : null,
          date: new Date(s.created_at).toLocaleDateString(),
        })),
        ...this.confirmedStatuses.map(s => ({
          label: 'Confirmed by:',
          name: this.creatorLabelPdf(s),
          // name: s.creator ? s.creator.name : s.creator_id,
          attachment: s.attachment ? this.signature + s.attachment : null,
          date: new Date(s.created_at).toLocaleDateString(),
        })),
      ];


      // Helper: format date like "15-10-2025"
      const formatDate = (dateStr) => {
        if (!dateStr) {
          return '';
        }
        const date = new Date(dateStr);
        if (isNaN(date)) {
          return dateStr;
        } // fallback if invalid
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      };
      const dayDifference = (start, end) => {
        if (!start || !end) {
          return '';
        }
        const startDate = new Date(start);
        const endDate = new Date(end);
        if (isNaN(startDate) || isNaN(endDate)) {
          return '';
        }
        const diffTime = endDate - startDate;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
        return diffDays;
      };
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 14;
      const title = 'TANZANIA PORTS AUTHORITY';
      const subTitle = 'LABOUR UTILIZATION SHEET';
      // Helper: draw header on each page
      const drawHeader = () => {
        if (leftLogo) {
          doc.addImage(leftLogo, 'PNG', margin, 10, 25, 25);
        }
        if (rightLogo) {
          doc.addImage(rightLogo, 'PNG', pageWidth - 40, 10, 25, 25);
        }
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(title, pageWidth / 2, 25, { align: 'center' });
        doc.setFontSize(12);
        doc.text(subTitle, pageWidth / 2, 35, { align: 'center' });

        // Small divider line
        doc.setLineWidth(0.2);
        doc.line(15, 38, pageWidth - 15, 38);
        doc.setFontSize(10);
      };

      // Draw header on the first page
      drawHeader();

      let startY = 50;

      this.invoice_types.forEach((type, tIndex) => {
        const itemsOfType = this.filteredItems.filter(i => i.invoice_type === type);
        if (!itemsOfType.length) {
          return;
        }

        // === Section Heading ===
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Station:', 14, startY);
        doc.setFont('helvetica', 'normal');
        doc.text(` ${this.invoice.department.port.station.name}`, 60, startY);
        doc.setFont('helvetica', 'bold');
        doc.text('Port:', 114, startY);
        doc.setFont('helvetica', 'normal');
        doc.text(`${this.invoice.department.port.name}`, 130, startY);
        startY += 6;
        doc.setFont('helvetica', 'bold');
        doc.text('Department:', 14, startY);
        doc.setFont('helvetica', 'normal');
        doc.text(this.invoice.department.name, 60, startY);
        startY += 6;
        doc.setFont('helvetica', 'bold');
        doc.text('Utilization Sheet No.:', 14, startY);
        doc.setFont('helvetica', 'normal');
        doc.text(this.invoice.invoice_number, 60, startY);
        startY += 6;

        const start = this.filters.start_date || this.invoice.start_date;
        const end = this.filters.end_date || this.invoice.end_date;
        const days = dayDifference(start, end);
        doc.setFont('helvetica', 'bold');
        doc.text(
            'Date:',
            14,
            startY
        );
        doc.setFont('helvetica', 'normal');
        doc.text(
            `${formatDate(start)} to ${formatDate(end)} (${days} days)`,
            60,
            startY
        );
        startY += 6;
        doc.setFont('helvetica', 'bold');
        doc.text('Guidance Type:', 14, startY);
        doc.setFont('helvetica', 'normal');
        doc.text(`${type}`, 60, startY);
        startY += 10;

        // === Table Data ===
        const columns = [
          'SN',
          'Labourer_Name',
          'Labourer ID',
          'TakeOn Number',
          'Guidance Number',
          'Date',
          'Workplace',
          'Activity',
          'Shift',
          'Worked Hours',
          'Rate(TZS)',
          'Cost(TZS)',
        ];

        const rows = itemsOfType.map((item, index) => [
          index + 1,
          item.labourer
              ? `${item.labourer.first_name} ${item.labourer.middle_name} ${item.labourer.last_name}`
              : '',
          item.labourer && item.labourer.vendor_record
              ? `${item.labourer.vendor_record.vendor_labourer_number}`
              : '',
          item.labourerSchedule ? item.labourerSchedule.takeon_number : '',
          item.guidance ? item.guidance.guidance_number : '',
          formatDate(item.date),
          item.workplace,
          item.activity,
          item.name,
          item.quantity,
          this.currencyFormat(item.rate),
          this.currencyFormat(item.quantity * item.rate),
        ]);

        // Add subtotal
        const subtotal = itemsOfType.reduce((sum, i) => sum + i.quantity * i.rate, 0);
        const vat = this.invoice.vat;
        const vat_rate = this.invoice.vat_rate;
        const grandTotal = this.invoice.vat+this.invoice.amount;
        rows.push(['', '', '', '', '','',  '','Subtotal', '', '','', this.currencyFormat(subtotal)]);
        rows.push(['', '', '', '', '','',  '', 'VAT('+vat_rate+'%)','', '','', this.currencyFormat(vat)]);
        rows.push(['', '', '', '', '','',  '','Grand Total', '', '','', this.currencyFormat(grandTotal)]);

        // === Render Table ===
        doc.autoTable({
          head: [columns],
          body: rows,
          startY: startY,
          styles: {
            fontSize: 8,
            halign: 'center',
            valign: 'middle',
            lineColor: [0, 0, 0],
            lineWidth: 0.1,
          },
          headStyles: {
            fillColor: [200, 200, 200],
            textColor: 0,
          },

          margin: { left: 14, right: 14 },
          theme: 'grid',

          didParseCell: function (data) {
            const label = data.row.raw?.[7]; // column index where label is

            if (
                label === 'Subtotal' ||
                label?.startsWith('VAT') ||
                label === 'Grand Total'
            ) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [240, 240, 240]; // optional highlight
            }
          }
        });

        // Move down after table
        startY = doc.autoTable.previous.finalY + 15;

        // Add a small space between tables
        if (tIndex < this.invoice_types.length - 1) {
          doc.addPage();
          drawHeader();
          startY = 50;
        }
      });
      // After adding tables and page numbers
      addSignatures(doc, signatureData);
      // === Add Page Numbers ===
      const pageCount = doc.internal.getNumberOfPages();

      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(100);
        const pageText = `Page ${i} of ${pageCount}`;
        doc.text(pageText, doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
      }


      // === Save PDF ===
      //   doc.save(`Utilization_Sheet_${this.invoice.invoice_number}.pdf`);
      // --- Save PDF ---
      //  const fileName = `Voucher_${this.voucher.voucher_number || "Summary"}.pdf`;
      // --- Show PDF in iframe and enable download ---
      const pdfBlob = doc.output("blob");
      this.pdfBlob = pdfBlob;
      this.pdfPreviewUrl = URL.createObjectURL(pdfBlob);
    },
    goToViewVoucher (id) {
      this.$router.push({ name: 'View Utilization Sheet Summary', params: { id } });
    },
    creatorLabel(status) {
      if (!status) return ''

      const creator = status.creator

      // No creator object at all
      if (!creator) {
        return status.creator_id ?? ''
      }

      const model = creator.model

      if (model) {
        const firstName = typeof model.first_name === 'string'
            ? model.first_name.trim()
            : ''

        const lastName = typeof model.last_name === 'string'
            ? model.last_name.trim()
            : ''

        const checkNo = model.check_no
            ? `<br>(C/N: ${model.check_no})`
            : ''

        const fullName = [firstName, lastName].filter(Boolean).join(' ')

        //  All safe fallbacks
        if (fullName) {
          return `${fullName}${checkNo}`
        }

        if (checkNo) {
          return `Unknown User${checkNo}`
        }

        return creator.name || 'Unknown User'
      }

      // Creator exists but no model
      return creator.name || 'Unknown User'
    },
    creatorLabelPdf(status) {
      if (!status) return ''

      const creator = status.creator

      // No creator object at all
      if (!creator) {
        return status.creator_id ?? ''
      }

      const model = creator.model

      if (model) {
        const firstName = typeof model.first_name === 'string'
            ? model.first_name.trim()
            : ''

        const lastName = typeof model.last_name === 'string'
            ? model.last_name.trim()
            : ''

        const checkNo = model.check_no
            ? `\n(C/N: ${model.check_no})`
            : ''

        const fullName = [firstName, lastName].filter(Boolean).join(' ')

        //  All safe fallbacks
        if (fullName) {
          return `${fullName}${checkNo}`
        }

        if (checkNo) {
          return `Unknown User${checkNo}`
        }

        return creator.name || 'Unknown User'
      }

      // Creator exists but no model
      return creator.name || 'Unknown User'
    },
    goToEdit (id) {
      this.$router.push({ name: 'Edit Utilization Sheet', params: { id } });
    },
    async generatePdf() {

      this.generatingPdf = true;
      this.pdfReady = false;

      await this.$store.dispatch('invoices/generatePdf', {
        id: this.invoice.id
      });

      this.waitForPdf();
    },

    async previewPdf() {
      this.downloading=true;
      const response = await this.$store.dispatch(
          'invoices/downloadPdf',
          {
            id: this.invoice.id,
            preview: true
          }
      );

      const url = window.URL.createObjectURL(
          new Blob([response.data], { type: 'application/pdf' })
      );
this.pdfPreviewUrl=url;
this.downloading=false;
    //  window.open(url, '_blank');
    },
    async waitForPdf() {

      this.generatingPdf = true;

      const timer = setInterval(async () => {

        const response = await this.$store.dispatch(
            'invoices/downloadPdfStatus', {
              id: this.invoice.id
            });

        if (response.status === 'COMPLETED') {

          clearInterval(timer);

          this.generatingPdf = false;
          this.pdfReady = true;
         // this.openPdf();
        }

        if (response.status === 'FAILED') {

          clearInterval(timer);

          this.generatingPdf = false;

          this.notifyError(response.error);
        }

      }, 3000);
    },
    async openPdf() {
      try {

        const response = await this.$store.dispatch(
            'invoices/downloadPdf',
            {
              id: this.invoice.id,
              preview: true
            }
        );

        const blob = new Blob([response.data], {
          type: 'application/pdf'
        });

        const url = window.URL.createObjectURL(blob);

        window.open(url, '_blank');

      } catch (e) {
        this.notifyError('Unable to open PDF.');
      }
    },
    // async previewPdf() {
    //   try {
    //     const response = await this.$store.dispatch(
    //         'invoices/downloadPdf',
    //         {
    //           id: this.invoice.id,
    //           preview: true
    //         }
    //     );
    //
    //     // Clean up the previous blob URL
    //     if (this.pdfPreviewUrl) {
    //       URL.revokeObjectURL(this.pdfPreviewUrl);
    //     }
    //
    //     const blob = new Blob([response.data], {
    //       type: 'application/pdf'
    //     });
    //
    //     this.pdfPreviewUrl = URL.createObjectURL(blob);
    //
    //   } catch (e) {
    //     this.notifyError('Unable to preview PDF.');
    //   }
    // },

    // async downloadPdf() {
    //   const response = await this.$store.dispatch(
    //       'invoices/downloadPdf',
    //       {
    //         id: this.invoice.id
    //       }
    //   );
    //
    //   const blob = new Blob([response.data], {
    //     type: 'application/pdf'
    //   });
    //
    //   const link = document.createElement('a');
    //
    //   link.href = URL.createObjectURL(blob);
    //   link.download = `Invoice-${this.invoice.invoice_number}.pdf`;
    //
    //   link.click();
    //
    //   URL.revokeObjectURL(link.href);
    // },
    async downloadPdf() {
     this.downloading=true;
      const response = await this.$store.dispatch(
          'invoices/downloadPdf',
          {
            id: this.invoice.id,
            preview: false
          }
      );

      const url = window.URL.createObjectURL(
          new Blob([response.data], { type: 'application/pdf' })
      );

      const link = document.createElement('a');

      link.href = url;
      link.download = `${this.invoice.invoice_number}.pdf`;

      link.click();

      window.URL.revokeObjectURL(url);
      this.downloading=false;
    },
    // Paginated items for current type
    paginatedInvoiceItems(type) {
      const filtered = this.filteredInvoiceTypes(type);
      if (!filtered || !Array.isArray(filtered)) return [];
      const start = (this.pagination.currentPage - 1) * this.pagination.perPage;
      const end = start + this.pagination.perPage;
      return filtered.slice(start, end);
    },
    totalItems(type) {
      const filtered = this.filteredInvoiceTypes(type);
      return filtered ? filtered.length : 0;
    },

  },

  computed: {
    statusClass () {
      switch (this.invoice?.status) {
        case 'Confirmed':
          return 'status-confirmed'
        case 'Approved':
          return 'status-approved'

        case 'Reviewed':
          return 'status-reviewed'

        case 'Created':
          return 'status-created'

        case 'Rejected':
          return 'status-rejected'

        default:
          return 'status-draft'
      }
    },
    availableLabourersForSelect () {
      return this.availableLabourers.map(item => ({
        ...item,
        full_name: [
          item.first_name,
          item.middle_name,
          item.last_name
        ].filter(Boolean).join(' ')
      }))
    },

    ...Vuex.mapState({
      me: state => state.profile.me
    }),

    simpleTimeline () {
      if (this.responsive) {
        return 'simple';
      }
      return '';
    },
    sort () {
      if (this.sortation.order === 'desc') {
        return `-${this.sortation.field}`;
      }
      return this.sortation.field;
    },

    filteredStatuses () {
      return this.statuses
          .filter(s => new Date(s.created_at) <= new Date())
          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at)
          );
    },
    totalAmount () {
      var total = this.items.reduce((total_amount, item) => item.rate * item.quantity + total_amount  ,0);
      var vat = this.invoice.vat;
      var grandTotal = total + vat;
      return grandTotal;
    },
    filteredItems () {
      return this.items.filter(item => {
        const matchesDate =
            (!this.filters.start_date ||
                new Date(item.date) >= new Date(this.filters.start_date)) &&
            (!this.filters.end_date ||
                new Date(item.date) <= new Date(this.filters.end_date));

        const matchesLabourer =
            !this.filters.labourer ||
            (item.labourer && item.labourer.id === this.filters.labourer);

        const matchesShift =
            !this.filters.shift || item.name === this.filters.shift;

        const matchesWorkplace =
            !this.filters.workplace || item.workplace === this.filters.workplace;

        return matchesDate && matchesLabourer && matchesShift && matchesWorkplace;
      });
    },
    createdStatuses() {
      return this.filteredStatuses.filter(
          status => status.name === 'Created' && status.deleted_at === null
      );
    },
    reviewedStatuses() {
      return this.filteredStatuses.filter(
          status => status.name === 'Reviewed' && status.deleted_at === null
      );
    },
    certifiedStatuses() {
      return this.filteredStatuses.filter(
          status => status.name === 'Certified' && status.deleted_at === null
      );
    },
    approvedStatuses() {
      return this.filteredStatuses.filter(
          status => status.name === 'Approved' && status.deleted_at === null
      );
    },
    confirmedStatuses() {
      return this.filteredStatuses.filter(
          status => status.name === 'Confirmed' && status.deleted_at === null
      );
    },

    from() {
      return this.totalItems === 0 ? 0 : (this.pagination.currentPage - 1) * this.pagination.perPage;
    },
    to() {
      return Math.min(this.from + this.pagination.perPage, this.totalItems);
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.pagination.perPage);
    }

  },
  watch: {
    'invoice.pdf_status': {
      immediate: true,
      handler(status) {

        this.pdfReady = status === 'COMPLETED';
        this.pdfStatus = status;

      }
    },
    filters: {
      deep: true,
      handler() {
        this.pagination.currentPage = 1;
      }
    },
    'pagination.perPage'(newVal) {
      this.pagination.currentPage = 1;
    },
    invoice_types(newVal) {
      if (newVal.length > 0 && !this.currentType) {
        this.currentType = newVal[0];
      }
    },
    query: {
      handler: 'getListDebounced',
      immediate: true,
    },
    sortation: {
      handler() {
        this.get();
      },
      deep: true,
    },

    pagination: {
      handler() {
        // local pagination, no API call needed
      },
      deep: true,
    }
  },

  mounted () {
    console.log(this.invoice);
    console.log('Iweeeee')
    this.pdfReady = this.invoice.pdf_status === 'COMPLETED';
    this.pdfStatus = this.invoice.pdf_status;
    this.onResponsiveInverted();
    window.addEventListener('resize', this.onResponsiveInverted);
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.onResponsiveInverted);
  }

};

</script>

<style lang="scss" scoped>

.seperator h5 {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 1em;
}

.seperator h5::before,
.seperator h5::after {
  content: "";
  display: block;
  flex-grow: 1;
  height: 1px;
  background: #ccc;
}

.seperator h5 span {
  padding: 0 2em;
}

.text-center {
  text-align: center;
}

.places-buttons + div .md-button:not(:last-child) {
  margin-right: 1px;
}

.modal-container {
  max-width: 1000px !important;
}
.pdf-preview-container {
  position: relative;
  margin-top: 20px;
}

.close-btn {
  position: absolute;
  top: 15px;
  right: 20px;
  background: #f44336!important;
  color: white;
  border-radius: 50%;
  z-index: 10;
}

.close-btn:hover {
  background: #d32f2f;
}
.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 99999;
}

.loading-text {
  margin-top: 12px;
  font-size: 14px;
  color: #555;
}
.filter-card {
  margin-bottom: 20px;
}

.filter-label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  font-size: 13px;
}

.filter-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
}
.sheet-summary-table {
  text-align: left;
}

/* HEADER */
.header {
  display: flex;
  align-items:flex-start;
  gap: 10px;
}

.title {
  font-size: 17px;
  font-weight: 600;
}

.sub {
  font-size: 12px;
  color: #777;
}

/* TABLE */
.info-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}

.info-table td {
  padding: 6px 6px;
  font-size: 13px;
  vertical-align: middle;
}

.info-table tr {
  border-bottom: 1px solid #eee;
}

/* ICON */
.info-table md-icon {
  font-size: 18px;
  color: #2e7d32;
}

/* LABEL COLUMN */
.info-table td:nth-child(2),
.info-table td:nth-child(5) {
  color: #777;
  width: 90px;
}

/* VALUE */
.info-table td:nth-child(3),
.info-table td:nth-child(6) {
  font-weight: 600;
}

/* ACTIONS */
.actions {
  display: flex;
  justify-content: flex-start;
  gap: 8px;
}

</style>
